// Page-view maths for the chart-surface sheets (the Guitar Pro page model):
// the sheet as side-by-side paper pages that fit the screen, instead of one
// vertical strip. Pure — the component measures, this module decides — so the
// plan is unit-testable without a DOM.
//
// Two rulings encoded here rather than left to the component:
//
//  - Paper size is a property of the VIEW, never of the tune: columns come
//    from the available width alone, and a one-page blues on a wide screen
//    gets the same page width a two-page standard does. Re-widening the lone
//    page would make the paper a different size on every tune.
//  - Pages fill sequentially (page 1 full, the last page partial), exactly
//    like a printed book — not balanced columns, which would reshuffle page
//    1's content every time the window resizes.

/** Gutter between page cards, px. */
export const SHEET_PAGE_GAP = 20;

/** Never narrower than the workspace's own compact breakpoint (700px). The
 * caller normally RAISES this to the chart's legibility breakpoint
 * (`halfWidthBreakpoint`), so a page always carries the authored 4-bar grid:
 * a bigger Chord size then means wider pages and fewer of them — the Guitar
 * Pro zoom trade — never the same page count with the grid silently halved
 * (measured: at the default 125% a 780px page fell under the breakpoint,
 * reflowed to 2 bars/line, and a spread showed FEWER bars than the strip). */
export const MIN_SHEET_PAGE_WIDTH = 700;

/** One page never wider than the classic capped canvas (1180px `.content`
 * minus its 2×32px padding), so a single page reads exactly like the sheet
 * everyone already knows. */
export const MAX_SHEET_PAGE_WIDTH = 1116;

export interface SheetPageInput {
  /** Width available to the whole spread, px. */
  availWidth: number;
  /** Narrowest acceptable page (default MIN_SHEET_PAGE_WIDTH; see above). */
  minPageWidth?: number;
  /** Height a page can give to grid rows (the band minus page chrome), px. */
  contentHeight: number;
  /** Page 1's own budget when it carries the masthead ON the paper — the
   * printed-score convention: title/composer head the first page and the
   * later pages reclaim that space rather than reserving an empty band.
   * Defaults to `contentHeight` (no masthead inside the paper). */
  firstPageContentHeight?: number;
  /** Total layout rows in the sheet (from computeLayout). */
  totalRows: number;
  /** Measured top-to-top distance between grid rows (row height + row-gap), px.
   * Non-positive = not measured yet: everything lands on one page until it is. */
  rowStride: number;
  /** The grid's row-gap, px — the last row on a page pays no gap. */
  rowGap: number;
}

export interface SheetPagePlan {
  /** Page columns the spread renders (never more than there are pages). */
  columns: number;
  pageWidth: number;
  pageCount: number;
  /** Layout-row windows per page, [start, end). */
  windows: [number, number][];
}

/** The width half of the plan on its own: page width is needed BEFORE row
 * counts exist, because cells-per-row (and so the row count) follows from it. */
export function sheetPageWidth(
  availWidth: number,
  minPageWidth: number = MIN_SHEET_PAGE_WIDTH,
): { fit: number; pageWidth: number } {
  const fit = Math.max(
    1,
    Math.floor((availWidth + SHEET_PAGE_GAP) / (minPageWidth + SHEET_PAGE_GAP)),
  );
  const pageWidth = Math.min(
    MAX_SHEET_PAGE_WIDTH,
    Math.max(1, (availWidth - (fit - 1) * SHEET_PAGE_GAP) / fit),
  );
  return { fit, pageWidth };
}

/** Guards a measure→setState loop against LAYOUT FEEDBACK — a measurement
 * that genuinely alternates because the plan it produces changes the very
 * thing being measured (a masthead that wraps at one spread width and not
 * the other; a classic scrollbar appearing when the page count grows). The
 * "same value → don't set" guard cannot help there: the values really do
 * differ on every pass, React's nested-update counter climbs, and at 50 it
 * unmounts the whole tree (user-found twice: "resizing results in a blank
 * screen", then the same crash on a cold tune load). Two rules, both scoped
 * to one settling burst:
 *  - a change BACK to the state before last, inside the burst, is a
 *    period-2 cycle: hold the current layout;
 *  - more than MAX_MEASURE_CHANGES changes inside one burst is a runaway of
 *    any shape: hold until the burst expires.
 * A held layout is always valid (cards are min-height, content never
 * clips); the next real event lands outside the window and measurement
 * proceeds normally — so a genuine quick resize back and forth is adopted
 * once the burst is over. */
export interface MeasureGate {
  times: number[];
  current: string | null;
  previous: string | null;
}
export const MEASURE_SETTLE_MS = 200;
export const MAX_MEASURE_CHANGES = 5;

export function createMeasureGate(): MeasureGate {
  return { times: [], current: null, previous: null };
}

export function measureSignature(values: readonly number[]): string {
  return values.map((v) => Math.round(v)).join('|');
}

/** What the gate decided about a measurement:
 *  - `admitted`  — adopt it (the gate records it);
 *  - `unchanged` — the layout already reflects it; do nothing;
 *  - `cycling`   — it bounced back to the state before last inside the
 *                  settling burst: a feedback loop, so HOLD (and do not
 *                  retry, or the hold would just become a slow flip-flop);
 *  - `busy`      — too many changes in this burst. Unlike a cycle this is
 *                  ordinary (a drag emits a resize per frame), so the caller
 *                  MUST re-measure once the burst expires: the last event of
 *                  a drag lands in a full burst, and without a retry the
 *                  layout stays stuck on a mid-drag measurement — pagination
 *                  frozen at the wrong size, or dropped entirely when the
 *                  held width sits under the page minimum (user-found:
 *                  "still breaking on resizing — losing the paging"). */
export type MeasureVerdict = 'admitted' | 'unchanged' | 'cycling' | 'busy';

export function admitMeasure(gate: MeasureGate, signature: string, now: number): MeasureVerdict {
  if (signature === gate.current) return 'unchanged';
  const times = gate.times.filter((t) => now - t < MEASURE_SETTLE_MS);
  if (times.length > 0 && signature === gate.previous) {
    gate.times = times;
    return 'cycling';
  }
  if (times.length >= MAX_MEASURE_CHANGES) {
    gate.times = times;
    return 'busy';
  }
  times.push(now);
  gate.times = times;
  gate.previous = gate.current;
  gate.current = signature;
  return 'admitted';
}

/** BAND-FIT or PAPER, from the content alone.
 *
 * Fitting pages to the band is the music-stand reading: thin chrome, whole
 * tune on screen. But where one slice is tall relative to the band, that rule
 * yields pages of ONE slice — a strip cut into cards, not a book (measured:
 * Score Studio's editor chrome takes ~470px, so a 45-bar standard came out as
 * ten one-system pages; the workbench's lens overlays stack grips above every
 * system and did the same). So when the band cannot hold the first TWO
 * slices, the page falls back to paper proportions and the spread scrolls,
 * like the reference editor.
 *
 * The first two slices, not an average: page 1 always starts at slice 0, so
 * that pair is stable as pagination reflows. And the decision cannot
 * oscillate — slice heights follow page WIDTH and the engraving, never page
 * height. */
export function needsPaperPages(spans: readonly number[], bandContent: number): boolean {
  return spans.length > 1 && spans[0] + spans[1] > bandContent;
}

/** Sequential packing for content whose slices VARY (the score's engraved
 * systems, and — with uniform slices — the chart's grid rows): each page
 * takes slices while they fit, and a slice taller than a page still gets a
 * page of its own. Same book rule throughout — sequential fill, never
 * balancing. `budget` may be a FUNCTION of the page index, which is how the
 * masthead earns its keep: page 1 is shorter because the title sits on it,
 * later pages get the full paper. */
export function packSpans(
  spans: readonly number[],
  budget: number | ((pageIndex: number) => number),
): [number, number][] {
  if (spans.length === 0) return [[0, 0]];
  const budgetFor = typeof budget === 'function' ? budget : () => budget;
  const windows: [number, number][] = [];
  let start = 0;
  let used = 0;
  let page = 0;
  for (let i = 0; i < spans.length; i++) {
    if (i > start && used + spans[i] > budgetFor(page)) {
      windows.push([start, i]);
      start = i;
      used = 0;
      page++;
    }
    used += spans[i];
  }
  windows.push([start, spans.length]);
  return windows;
}

export function planSheetPages({
  availWidth,
  minPageWidth,
  contentHeight,
  firstPageContentHeight,
  totalRows,
  rowStride,
  rowGap,
}: SheetPageInput): SheetPagePlan {
  const { fit, pageWidth } = sheetPageWidth(availWidth, minPageWidth);
  // Uniform slices through the same packer as the score's varying ones, so
  // the two surfaces cannot disagree about how a book fills. R rows consume
  // R·stride − gap (the last row keeps its height, drops its gap), which is
  // exactly a budget of contentHeight + gap against slices of one stride.
  const windows =
    rowStride > 0 && totalRows > 0
      ? packSpans(new Array<number>(totalRows).fill(rowStride), (page) =>
          Math.max(
            rowStride,
            (page === 0 ? (firstPageContentHeight ?? contentHeight) : contentHeight) + rowGap,
          ),
        )
      : ([[0, Math.max(0, totalRows)]] as [number, number][]);
  return {
    columns: Math.min(fit, windows.length),
    pageWidth,
    pageCount: windows.length,
    windows,
  };
}
